import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { browserHistory, RouteComponentProps } from 'react-router';

import { Paper, Theme, Toolbar } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import moment from 'moment';

import {
  GridMessagingActivitySummary,
  GridMessagingActivityTables,
  WrapperQueryTableMessages,
  WrapperQueryTableReminders,
} from 'embeds/deprecated/queries';

import { Tab, Tabs, Typography, EmptyStateNextReminders, EmptyStatePastMessages } from 'embeds/components';

const styles = ({ spacing: { unit }, palette: { background }, zIndex: { tooltip } }: Theme) =>
  createStyles({
    root: {
      width: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      padding: `${unit * 2}px ${unit * 3}px 0`,
      width: '100%',
      marginBottom: unit * 4,
    },
    contentSummary: {
      paddingLeft: unit * 3,
      paddingRight: unit * 3,
    },
    summaryCardsWrapper: {
      marginTop: unit * 2,
      marginBottom: unit * 3,
    },
    emptyState: {
      position: 'fixed',
      top: 250,
      right: 0,
      bottom: 0,
      background: background.default,
      zIndex: tooltip,
    },
  });

const messages = defineMessages({
  title: {
    id: 'embeds.MessagingActivityScreen.title',
    defaultMessage: 'Messaging Activity',
  },
  summary: {
    id: 'embeds.MessagingActivityScreen.summary',
    defaultMessage: 'Today',
  },
  summaryTitle: {
    id: 'embeds.MessagingActivityScreen.summaryTitle',
    defaultMessage: 'Today, {date}',
  },
  messages: {
    id: 'embeds.MessagingActivityScreen.messages',
    defaultMessage: 'Past Messages',
  },
  queued: {
    id: 'embeds.MessagingActivityScreen.queued',
    defaultMessage: 'Next Reminders',
  },
});

const dateFrom = moment()
  .startOf('day')
  .valueOf();
const sendAtFrom = moment().valueOf();
const dateTo = moment()
  .endOf('day')
  .valueOf();

export enum MessagingActivityScreenTab {
  Summary = 'summary',
  Queued = 'queued',
  Messages = 'messages',
}

type RouterProps = {
  tab: MessagingActivityScreenTab;
};

export type MessagingActivityScreenRedirect =
  | MessagingActivityScreenTab
  | 'sentMessages'
  | 'undeliveredMessages'
  | 'receivedMessages';

export interface MessagingActivityScreenProps
  extends WithStyles<typeof styles>,
    RouteComponentProps<RouterProps, RouterProps> {}

const MessagingActivityScreen: React.FC<MessagingActivityScreenProps> = ({ classes, params: { tab } }) => {
  const { formatMessage } = useIntl();

  const handleRedirect = useCallback((value: MessagingActivityScreenRedirect) => {
    let path = `/embed/messaging-activity/`;
    if (value === 'sentMessages') {
      path += 'messages?status=sent,delivered&type=outbound';
    } else if (value === 'undeliveredMessages') {
      path += 'messages?status=undelivered';
    } else if (value === 'receivedMessages') {
      path += 'messages?type=inbound';
    } else {
      path += value;
    }
    browserHistory.push(path);
  }, []);

  return (
    <div className={classes.root}>
      <Paper className={classes.header} elevation={1} square>
        <Typography variant="customHeader" color="primary" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Tabs variant="fullWidth" disableGutters value={tab} onChange={(event, value) => handleRedirect(value)}>
          <Tab label={formatMessage(messages.summary)} value={MessagingActivityScreenTab.Summary} />
          <Tab label={formatMessage(messages.messages)} value={MessagingActivityScreenTab.Messages} />
          <Tab label={formatMessage(messages.queued)} value={MessagingActivityScreenTab.Queued} />
        </Tabs>
      </Paper>
      <>
        {tab === MessagingActivityScreenTab.Summary && (
          <div className={classes.contentSummary}>
            <Typography variant="h6StrongBold" paragraph>
              {formatMessage(messages.summaryTitle, { date: moment().format('LL') })}
            </Typography>
            {/* TODO move to `queries/WrapperQueryMessagingActivitySummary` */}
            <div className={classes.summaryCardsWrapper}>
              <GridMessagingActivitySummary
                dateFrom={dateFrom}
                sendAtFrom={sendAtFrom}
                dateTo={dateTo}
                onRedirect={handleRedirect}
              />
            </div>
            <GridMessagingActivityTables
              dateFrom={dateFrom}
              sendAtFrom={sendAtFrom}
              dateTo={dateTo}
              onRedirect={handleRedirect}
            />
          </div>
        )}
        {tab === MessagingActivityScreenTab.Messages && (
          <>
            <Toolbar>
              <Typography variant="h6StrongBold" paragraph>
                {formatMessage(messages.messages)}
              </Typography>
            </Toolbar>
            <WrapperQueryTableMessages
              padding="checkbox"
              renderEmptyState={() => <EmptyStatePastMessages className={classes.emptyState} />}
            />
          </>
        )}
        {tab === MessagingActivityScreenTab.Queued && (
          <>
            <Toolbar>
              <Typography variant="h6StrongBold" paragraph>
                {formatMessage(messages.queued)}
              </Typography>
            </Toolbar>
            <WrapperQueryTableReminders
              padding="checkbox"
              renderEmptyState={() => <EmptyStateNextReminders className={classes.emptyState} />}
            />
          </>
        )}
      </>
    </div>
  );
};

export default withStyles(styles)(MessagingActivityScreen);
